/**
 * Running a batch from the browser — in slices, resumably, and without ever guessing.
 *
 * THE FAULT THIS IS ABOUT
 * -----------------------
 * A batch was three HTTP requests: create, validate, execute. Each one had to contain all of its
 * work, and the work is one real render per row per sealed language. Measured on the instance that
 * reported the fault, a row costs about 1.8 s, so a hundred rows is roughly three minutes of
 * validation — and the client gives up at thirty seconds. What the operator saw was «Sunucuya
 * ulaşılamadı»; what the server did was finish the validation, mark the batch `planned`, and wait
 * for an execute nobody was left to ask for. The batch sat at «planned / 0». Ten rows worked
 * because ten rows fitted: ~18 s, inside the timeout, and the e-mails went out.
 *
 * WHAT THIS MODULE DOES INSTEAD
 * -----------------------------
 * It asks for slices. The server answers each call inside its own budget and says what is left, so
 * no single request has to survive the whole job, and every row it finished is already persisted.
 * Progress is the server's own count, not an animation.
 *
 * AND IT NEVER TURNS «I DO NOT KNOW» INTO «IT FAILED». A request that is cut off says nothing about
 * whether the work happened: the batch id is kept, the batch is read back, and the loop continues
 * from the state the SERVER reports. Only when the server itself refuses is that a failure.
 *
 * NOTHING HERE CREATES A SECOND BATCH. The id of the batch an attempt opened is remembered for that
 * attempt, so a second press and a page refresh both continue the one that exists — and because
 * every row's issuance is keyed by `<batchId>:<rowNo>`, continuing can never issue a row twice or
 * send its e-mail twice.
 */
import type { ApiClient } from '../../api/client';
import { isApiError } from '../../api/errors';
import { createBatch, executeBatch, readBatch, validateBatch } from '../../api/internal';
import type { BatchOptions } from '../../api/types';

/** What the screen draws while a batch runs: the server's own counts. */
export interface BatchRunProgress {
  phase: 'creating' | 'validating' | 'issuing' | 'reconciling';
  /** Rows the server has finished in this phase. */
  done: number;
  /** Rows the file has. 0 until the server has said. */
  total: number;
}

export type BatchRunOutcome =
  /** Every row processed and the batch committed by the server. */
  | { kind: 'committed'; batchId: string; issued: number; failed: number; skipped: number }
  /**
   * The work is real and unfinished, and this browser could not keep asking — the connection kept
   * breaking. NOT a failure: the batch and its rows are on the server, and the same attempt can be
   * continued. The operator is told exactly that.
   */
  | { kind: 'interrupted'; batchId: string; processed: number; remaining: number }
  /** The server refused. `code` is its own, for a screen that wants to react to one. */
  | { kind: 'refused'; batchId: string | null; code: string; message: string };

export interface BatchRunHooks {
  onProgress?: (p: BatchRunProgress) => void;
}

/* ------------------------------------------------------------------ the attempt's own memory */

/**
 * WHICH ATTEMPT THIS IS — derived from the file, not from a counter.
 *
 * The memory below answers «did this attempt already open a batch?», and that question only has a
 * useful answer if the same attempt produces the same key twice. A random id does not survive a
 * refresh, and a refresh is exactly when an operator re-uploads the same list and presses again. So
 * the key is the file's own digest together with the release it is being issued from: the same
 * bytes for the same template in the same browsing session continue the batch that exists instead
 * of opening a second one.
 *
 * It is NOT a server-side guarantee and does not pretend to be one. The guarantee that a row is
 * never issued twice is the backend's, keyed by `<batchId>:<rowNo>`; across two different batches
 * it is the release's duplicate policy. This only keeps the ordinary case — press twice, refresh,
 * press again — from opening batches nobody asked for.
 */
export async function attemptKeyForFile(bytes: ArrayBuffer, releaseId: string): Promise<string | null> {
  try {
    const digest = await crypto.subtle.digest('SHA-256', bytes);
    const hex = [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
    return `${releaseId}:${hex}`;
  } catch {
    /* No subtle crypto (an insecure context): the run still works, it just cannot be resumed. */
    return null;
  }
}

const KEY_PREFIX = 'hpw.batch.attempt.';

/**
 * Browser storage, used for exactly one thing: «this attempt already opened batch X».
 *
 * `sessionStorage` because it belongs to this tab's visit and must not outlive it, and every access
 * is guarded: a private window, blocked site data or a thumbnail capture can make any of these
 * throw or answer empty, and a batch run must not depend on that. Losing the memory costs a second
 * batch at worst; it never issues a row twice, because the rows are keyed server-side.
 */
export function recallBatchId(attemptKey: string | null): string | null {
  if (!attemptKey) return null;
  try {
    return window.sessionStorage.getItem(KEY_PREFIX + attemptKey);
  } catch {
    return null;
  }
}

export function rememberBatchId(attemptKey: string | null, batchId: string): void {
  if (!attemptKey) return;
  try {
    window.sessionStorage.setItem(KEY_PREFIX + attemptKey, batchId);
  } catch {
    /* nothing: the run continues, it just cannot be resumed after a refresh */
  }
}

export function forgetBatchId(attemptKey: string | null): void {
  if (!attemptKey) return;
  try {
    window.sessionStorage.removeItem(KEY_PREFIX + attemptKey);
  } catch {
    /* nothing */
  }
}

/* ------------------------------------------------------------------ the run */

/** A transport failure says nothing about what the server did; a refusal does. */
const isTransportFailure = (e: unknown): boolean => isApiError(e) && e.kind === 'network';

/**
 * How many times in a row the connection may break before this browser stops asking.
 *
 * Not a retry of a mutation — every call here is safe to repeat — but a bound on how long a loop
 * keeps talking to a server it cannot reach. After this the outcome is `interrupted`, which names
 * the batch and what the server had done.
 */
const MAX_CONSECUTIVE_TRANSPORT_FAILURES = 3;

/** The batch this attempt is running: the one it already opened, or a new one. */
export async function openBatch(
  api: ApiClient,
  attemptKey: string | null,
  file: { bytes: ArrayBuffer; name: string; releaseId: string },
): Promise<string> {
  const known = recallBatchId(attemptKey);
  if (known) {
    /* Trust it only if the server still has it; a stale id from an earlier database must not
       silently swallow this run. */
    try {
      const seen = await readBatch(api, known);
      return seen.batch.id;
    } catch (e) {
      if (isTransportFailure(e)) throw e;
      forgetBatchId(attemptKey);
    }
  }
  /*
   * The key goes to the SERVER, not just into this tab's memory. The memory is a convenience — it
   * is gone in a private window and it is gone if the tab is closed — and it cannot help at all
   * with the two cases that matter: an answer that never arrives, and two starts at once. Those
   * are decided by the unique index behind `Idempotency-Key` (migration 026), so asking twice
   * returns the same batch rather than making a second one over the same hundred people.
   */
  const created = await createBatch(api, file.bytes, {
    filename: file.name,
    templateReleaseId: file.releaseId,
    format: /\.csv$/i.test(file.name) ? 'csv' : 'xlsx',
    ...(attemptKey ? { idempotencyKey: attemptKey } : {}),
  });
  rememberBatchId(attemptKey, created.batch.id);
  return created.batch.id;
}

/**
 * Validate, then execute, in slices, until the server says the batch is done.
 *
 * The two loops have the same shape: ask, read what the server says is left, ask again. Neither
 * holds state the server does not have, which is what makes an interrupted run resumable and a
 * repeated press harmless.
 */
export async function runBatch(
  api: ApiClient,
  batchId: string,
  plan: { columnMapping: Record<string, string>; options: BatchOptions },
  hooks: BatchRunHooks = {},
): Promise<BatchRunOutcome> {
  const progress = hooks.onProgress ?? ((): void => undefined);
  let failures = 0;

  /*
   * ---- WHAT THE SERVER ALREADY DID, BEFORE ANYTHING IS ASKED OF IT.
   *
   * This loop used to open by starting a validation, which on a batch that is already finished or
   * already half issued is the wrong act in two different ways: a committed batch would be
   * re-validated into a refusal and reported as a failure, and a partly issued one would have every
   * row put back to `pending` — cutting the issued rows loose from the certificates they produced
   * and dropping the e-mail design the run was pinned to. The server refuses that now; this asks
   * first so it never has to.
   */
  const known = await lastKnown(api, batchId);
  if (known?.status === 'committed') {
    progress({ phase: 'issuing', done: known.processed, total: known.total });
    return {
      kind: 'committed',
      batchId,
      issued: known.issued,
      failed: known.failed,
      skipped: known.skipped,
    };
  }
  /* Rows have been issued, or the dry run is finished: the work left is issuance, not validation. */
  let skipValidation = (known?.issued ?? 0) > 0 || known?.validation === 'complete';
  /* A dry run already under way is CONTINUED. Restarting would throw away the rows it finished. */
  let started = known?.validation === 'in_progress';
  let validatedTotal = known ? known.total - known.remaining : 0;
  let rows = known?.total ?? 0;
  /*
   * The run this driver's slices belong to. The server coins it on the first call and every later
   * slice sends it back, so a slice stalled behind a render cannot come back after the file was
   * validated again and have its verdicts merged into the newer run's report — it is refused.
   * Unknown until the first answer, except when continuing a dry run that was already under way,
   * where it comes from the report the batch is holding.
   */
  let runId = known?.validation === 'in_progress' ? known.runId : undefined;
  if (known)
    progress({ phase: skipValidation ? 'issuing' : 'validating', done: validatedTotal, total: rows });

  /* ---- the dry run. The first call starts it; the rest continue the same one. */
  while (!skipValidation) {
    try {
      const answer = await validateBatch(api, batchId, {
        columnMapping: plan.columnMapping,
        options: plan.options,
        ...(started ? { resume: true } : {}),
        ...(started && runId ? { runId } : {}),
      });
      failures = 0;
      started = true;
      runId = answer.report.runId ?? runId;
      rows = answer.report.summary.rows || rows;
      validatedTotal = rows - answer.remaining;
      progress({ phase: 'validating', done: validatedTotal, total: rows });
      if (answer.complete) {
        skipValidation = true;
        break;
      }
      if (answer.validated === 0)
        /* The server made no progress and did not say it was finished: asking again would spin. */
        return { kind: 'interrupted', batchId, processed: validatedTotal, remaining: answer.remaining };
    } catch (e) {
      if (!isTransportFailure(e)) return refusal(batchId, e);
      failures += 1;
      if (failures > MAX_CONSECUTIVE_TRANSPORT_FAILURES) {
        const seen = await lastKnown(api, batchId);
        return {
          kind: 'interrupted',
          batchId,
          processed: seen?.processed ?? validatedTotal,
          remaining: seen?.remaining ?? Math.max(rows - validatedTotal, 0),
        };
      }
      /*
       * The request was cut off; the work may well have happened. Read the batch and carry on from
       * what the server reports — a validation already in progress is continued, never restarted,
       * because restarting would throw away rows it had finished.
       */
      const seen = await lastKnown(api, batchId);
      if (seen) {
        progress({ phase: 'reconciling', done: seen.processed, total: seen.total });
        if (seen.total > 0) rows = seen.total;
        if (seen.status === 'committed')
          return {
            kind: 'committed',
            batchId,
            issued: seen.issued,
            failed: seen.failed,
            skipped: seen.skipped,
          };
        if (seen.status === 'planned' || seen.validation === 'complete') {
          /* The dry run finished while we were not listening. */
          skipValidation = true;
          break;
        }
        started = started || seen.validation === 'in_progress';
      }
    }
  }

  /* ---- the issuance. Same shape; `remaining` is the server's own count of planned rows. */
  let issued = 0;
  let failed = 0;
  let skipped = 0;
  failures = 0;
  for (;;) {
    try {
      const answer = await executeBatch(api, batchId);
      failures = 0;
      issued += answer.issued;
      failed += answer.failed;
      skipped += answer.skippedDuplicate;
      progress({ phase: 'issuing', done: Math.max(rows - answer.remaining, 0), total: rows });
      if (answer.remaining === 0) return { kind: 'committed', batchId, issued, failed, skipped };
      if (answer.issued + answer.failed + answer.skippedDuplicate + answer.alreadyIssued === 0)
        /* Nothing moved and rows remain — another worker holds them, or the slice could do none. */
        return {
          kind: 'interrupted',
          batchId,
          processed: rows - answer.remaining,
          remaining: answer.remaining,
        };
    } catch (e) {
      if (!isApiError(e)) return refusal(batchId, e);
      if (e.code === 'batch_already_committed') {
        /* Someone — an earlier press of this very button — already finished it. */
        const seen = await lastKnown(api, batchId);
        return { kind: 'committed', batchId, issued: seen?.issued ?? issued, failed, skipped };
      }
      if (!isTransportFailure(e)) return refusal(batchId, e);
      failures += 1;
      const seen = await lastKnown(api, batchId);
      if (seen?.status === 'committed')
        return { kind: 'committed', batchId, issued: seen.issued, failed, skipped };
      if (failures > MAX_CONSECUTIVE_TRANSPORT_FAILURES)
        return {
          kind: 'interrupted',
          batchId,
          processed: seen?.processed ?? 0,
          remaining: seen?.remaining ?? 0,
        };
      if (seen) progress({ phase: 'reconciling', done: seen.processed, total: seen.total });
    }
  }
}

function refusal(batchId: string, e: unknown): BatchRunOutcome {
  if (isApiError(e)) return { kind: 'refused', batchId, code: e.code, message: e.message };
  return { kind: 'refused', batchId, code: 'unexpected', message: '' };
}

/** What the server says about this batch right now, or `null` if it cannot be asked. */
export interface BatchServerState {
  status: string;
  total: number;
  processed: number;
  remaining: number;
  /** Rows that produced a credential — the ones a fresh validation must never undo. */
  issued: number;
  /** Rows the server settled without a credential. Reported as they are, never as zero. */
  failed: number;
  skipped: number;
  /** Where the dry run stands, read from the report the batch is holding. */
  validation: 'none' | 'in_progress' | 'complete';
  /**
   * Which run the stored report belongs to, so a slice that continues a dry run started before this
   * driver was even loaded can name it. Absent on reports written before run names existed, and
   * then nothing is sent — the server adopts whatever it is holding, as it always did.
   */
  runId: string | undefined;
}

async function lastKnown(api: ApiClient, batchId: string): Promise<BatchServerState | null> {
  try {
    const seen = await readBatch(api, batchId);
    const report = (seen.batch.dryRunReport ?? null) as { complete?: boolean; runId?: string } | null;
    /* The server groups the rows by status; a status it has none of is simply absent. */
    const settled = (status: string): number => seen.summary.rowStatus[status] ?? 0;
    return {
      status: seen.batch.status,
      total: seen.progress.total,
      processed: seen.progress.processed,
      remaining: seen.progress.remaining,
      /* The batch's own count of rows that produced a credential, from the server's grouping. */
      issued: settled('issued'),
      /*
       * A committed batch may well have settled rows WITHOUT a credential — a failed row, a
       * duplicate the policy declined to issue — and `completed_with_errors` is a real outcome. So
       * these are read from the same grouping rather than reported as zero by a code path that did
       * not do the work itself.
       */
      failed: settled('failed'),
      skipped: settled('skipped_duplicate') + settled('needs_resolution'),
      validation:
        report === null
          ? 'none'
          : report.complete === false
            ? 'in_progress'
            : /* A report with no `complete` flag is one written before slicing existed, and those
                 were always whole. */
              'complete',
      runId: report?.runId,
    };
  } catch {
    return null;
  }
}

/** The same read, for a screen that wants to reconcile without running anything. */
export async function reconcileBatch(
  api: ApiClient,
  batchId: string,
): Promise<{ status: string; total: number; processed: number; remaining: number; issued: number } | null> {
  return lastKnown(api, batchId);
}
